import SwitchBar from '../../components/switch-bar';
import { useClient, useUpdateClient } from '../../hooks/useClients';
import { HeaderOptions } from './clients-constants';
import HeadBack from './redirect';
import Header from '../../components/common/global-top-bar';
import { useEffect, useState } from 'react';
import { Button, Dialog, Input } from '../../components/ui';
import { toast } from 'react-toastify';
import { ToggleButton } from '../../components/common';
import RichTextEditor from '../../components/rich-text-editor/rich-text-editor';

export const SSOConfiguration = () => {
  const [settingsModal, setSettingsModal] = useState(false);
  const clientId = +localStorage.getItem('clientid')!;
  const { data: client } = useClient(clientId);
  const { updateClient } = useUpdateClient(clientId);
  const [isDirty, setIsDirty] = useState(false);
  const [trustedSSO, setTrustedSSO] = useState(client?.trusted_sso ?? false);
  const [acceptPolicyDescription, setAcceptPolicyDescription] = useState(client?.accept_policy_description ?? '');

  const clearSSOConfiguration = () => {
    updateClient.mutate(
      { sso_configuration: null },
      {
        onSuccess: () => {
          setSettingsModal(false);
        },
      },
    );
  };

  const handleSubmitSSOConfig = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const auth_url_value: String = (formData.get('authentication_url') as string).trim();
    if (auth_url_value === '') {
      toast.error('Enter an SSO authentication url or clear the SSO configuration to continue.');
      return;
    }
    const sso_configuration = {
      authentication_url: auth_url_value,
    };
    updateClient.mutate(
      { sso_configuration: { ...client?.sso_configuration, ...sso_configuration } },
      {
        onSuccess: () => {
          setSettingsModal(false);
        },
      },
    );
  };

  const handleSubmitTrustedSSOConfig = () => {
    updateClient.mutate(
      {
        trusted_sso: trustedSSO,
        accept_policy_description: acceptPolicyDescription !== '' ? acceptPolicyDescription : null,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
        },
      },
    );
  };

  useEffect(() => {
    if (client) {
      setTrustedSSO(client.trusted_sso);
      setAcceptPolicyDescription(client.accept_policy_description ?? '');
    }
  }, [client]);

  return (
    <div key={client?.id}>
      <Header heading={<HeadBack clientName={client?.name || ''} />} />
      <SwitchBar heading={HeaderOptions} position={6} />
      <div className="p-8">
        <div>
          {client?.sso_configuration && (
            <div className="w-2/3 p-3">
              Client is configured for SSO.
              <br />
              Members who onboard to this client will not be able to enter a password during onboarding or authenticate
              with our system unless the client supports SSO.
              <br />
              Click edit to clear the SSO configuration or edit the authentication URL.
            </div>
          )}
          {client?.sso_configuration
            ? Object.entries(client.sso_configuration).map(([key, value]) => (
                <div className="p-3" key={key}>
                  {`${key}: ${value}`}
                </div>
              ))
            : 'No SSO Configuration'}
        </div>
        <Button className="mt-8" onClick={() => setSettingsModal(true)} variant="primary">
          Edit
        </Button>
        <div className="mt-16 flex items-center justify-between border-t border-black pt-8">
          <h1 className="text-lg font-bold">Trusted SSO</h1>
          <Button variant="primary" onClick={() => handleSubmitTrustedSSOConfig()} disabled={!isDirty}>
            Save
          </Button>
        </div>
        <div className="flex w-1/4 items-center justify-between py-4">
          <label>Enable Trusted SSO</label>
          <ToggleButton
            status={trustedSSO}
            setStatus={(data) => {
              setIsDirty(true);
              setTrustedSSO(data);
            }}
            initialstatus={false}
            id="trustedSsoToggle"
          />
        </div>
        <div>
          <label className="mb-2 block">Accept Policies Description</label>
          <p className="mb-2 text-sm text-gray-500">
            This is the message that will be displayed on the T&C, Privacy Policy, and Marketing Opt-In page
            (mwa.com/accept-policies) for Trusted SSO users who have not accepted the current policies.
          </p>
          <RichTextEditor
            content={acceptPolicyDescription}
            onChange={(value) => {
              setIsDirty(true);
              setAcceptPolicyDescription(value);
            }}
            key={`accept-policy-description`}
          />
        </div>
        <Dialog className="w-2/3 p-4" open={settingsModal} onClose={() => setSettingsModal(false)}>
          <form onSubmit={handleSubmitSSOConfig}>
            <h3>SSO Configuration Options</h3>
            <Input
              key={client?.sso_configuration?.authentication_url}
              id="authentication_url"
              name="authentication_url"
              className="p-8"
              placeholder="SSO authentication url"
              onIconClick={() => {}}
              defaultValue={client?.sso_configuration?.authentication_url ?? undefined}
            />
            <div className="flex justify-between gap-x-4">
              <div>
                <Button
                  className="text-redSalsa border-redSalsa"
                  type="button"
                  onClick={() => clearSSOConfiguration()}
                  variant="secondary"
                >
                  Clear SSO Configuration
                </Button>
              </div>
              <div>
                <Button className="mx-4" type="button" onClick={() => setSettingsModal(false)} variant="secondary">
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Submit
                </Button>
              </div>
            </div>
          </form>
        </Dialog>
      </div>
    </div>
  );
};
